import React, { useContext, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "utils/useAuth/useAuth";
import { useSnackbar } from "notistack";

import { queryAgentDetail, getSuspensionRemarks, updateStatus } from "./model";
import s from "./AgentsDetail.module.scss";

import SimpleDialog from "components/SimpleDialog/SimpleDialog";
import AccountStatusDialog from "../../components/account-status/AccountStatusDialog";
import { isNullish } from "utils/utils";

import { PortalContext } from "App";
import InnerTabList from "components/InnerTabList/InnerTabList";
import AgentsDetailInfo from "./AgentsDetailInfo";
import AgentsDetailMerchants from "./AgentsDetailMerchants";

const PAGE_LOCALE_KEY = "page.agents.agents_detail.";
const DIALOG_LOCALE_KEY = "page.dialogs.";

import responseCodeMapping from "utils/response_code_mapping.json";

const AgentsDetail = ({ path, redirectFn }) => {
  const { t } = useTranslation();
  const { logout } = useAuth();

  const [selectedTab, setSelectedTab] = useState("detail_info");
  const { enqueueSnackbar } = useSnackbar();

  const stateContext = useContext(PortalContext);
  const { detailPageState } = stateContext;
  const [IsoAgentId, setIsoAgentId] = useState("");

  const AGENT_STATUS_INIT_STATE = {
    status: "",
    lockAccountType: "",
    remark: "",
  };

  const [statusRemarks, setStatusRemarks] = useState();
  const [agentDetailField, setAgentDetailField] = useState({ email: "" });

  const [detailLoading, setDetailLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const isInitRef = useRef(true);

  const [accountStatusDialogData, setAccountStatusDialogData] = useState(
    AGENT_STATUS_INIT_STATE,
  );
  const [isAccountStatusDialogOpen, setIsAccountStatusDialogOpen] =
    useState(false);

  useLayoutEffect(() => {
    const sessionState = JSON.parse(sessionStorage.getItem("state"));
    if (detailPageState && detailPageState[path]) {
      setIsoAgentId(detailPageState[path].isoAgentId);
      setAgentDetailField((state) => ({
        ...state,
        isoAgentId: detailPageState[path].isoAgentId,
        email: detailPageState[path].email,
        status: detailPageState[path].status,
      }));
    } else if (sessionState && sessionState[path]) {
      setIsoAgentId(sessionState[path].isoAgentId);
      setAgentDetailField((state) => ({
        ...state,
        isoAgentId: sessionState[path].isoAgentId,
        email: sessionState[path].email,
        status: sessionState[path].status,
      }));
    } else {
      if (IsoAgentId == "") setIsoAgentId("close");
    }
  }, [detailPageState[path]]);

  useLayoutEffect(() => {
    if (IsoAgentId == "close") {
      redirectFn("close", { tabId: path });
    } else {
      if (!!IsoAgentId && IsoAgentId !== "") {
        console.log("isoAgentId : ", IsoAgentId);
        initialFetchRemarks();

        if (!IsoAgentId) {
          //message "unknown payment id" will be shown
          setDetailLoading(false);
          return;
        }
      }
    }
  }, [IsoAgentId]);

  const innerTabContainer = () => {
    const tabList = [
      {
        key: "detail_info",
        label: t(PAGE_LOCALE_KEY + "detailTitle"),
        value: "detail_info",
      },
      {
        key: "detail_merchants",
        label: t(PAGE_LOCALE_KEY + "sectionMerchantsTitle"),
        value: "detail_merchants",
      },
    ];

    const changeSelectedTab = (event, innerTabName) => {
      setSelectedTab(innerTabName);
    };

    return (
      <InnerTabList
        selectedTab={selectedTab}
        handleChangeTab={changeSelectedTab}
        tabList={tabList}
      />
    );
  };

  const initialFetchDetails = async (getIsOudated) => {
    console.log("initialFetchDetails");
    setDetailLoading(true);
    const apiResponse = await queryAgentDetail(IsoAgentId);
    if (getIsOudated && getIsOudated()) return;
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            isInitRef.current = false;
            setAgentDetailField((state) => ({
              ...state,
              ...responseBody.data,
            }));
          } else {
            snackbar(
              t(PAGE_LOCALE_KEY + "fetchFail") + ": " + responseBody.message,
              "error",
            );
            setDatasource({
              total: 0,
              page: 0,
              size: 10,
              data: [],
            });
          }
        }
      }
    }
    setDetailLoading(false);
  };

  const initialFetchRemarks = async () => {
    console.log("initialFetchRemarks ");
    const apiResponse = await getSuspensionRemarks();

    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            setStatusRemarks(responseBody.data);
          } else {
            snackbar(
              t(PAGE_LOCALE_KEY + "fetchFail") + ": " + responseBody.message,
              "error",
            );
          }
        }
      }
    }
  };
  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  // ===================== status dialog function ===================== //

  const openAccountStatusDialog = (data) => {
    setAccountStatusDialogData(data);
    setIsAccountStatusDialogOpen(true);
  };

  const closeAccountStatusDialog = () => {
    restoreAccountStatusDialog();
    setIsAccountStatusDialogOpen(false);
  };

  const restoreAccountStatusDialog = () => {
    setAccountStatusDialogData(AGENT_STATUS_INIT_STATE);
  };

  const updatePayload = (fieldInput) => {
    setAccountStatusDialogData(fieldInput);
  };

  const accountStatusDialogValidationCheck = () => {
    if (accountStatusDialogData.status !== "ACTIVE") {
      const validationResult =
        !!accountStatusDialogData.reason &&
        !!accountStatusDialogData.reason !== "";
      if (!validationResult) {
        snackbar(
          t(DIALOG_LOCALE_KEY + "changeStatus.msgError") +
            " Reason must not be empty!",
          "error",
        );
      }
      return validationResult;
    }
    return true;
  };

  const updateResultCallback = async () => {
    if (accountStatusDialogValidationCheck()) {
      setSubmitting(true);
      const reasonList = statusRemarks[accountStatusDialogData.status];
      const remarkOthersKey = "OTHERS";

      let remarksend =
        reasonList && (reasonList[accountStatusDialogData.reason] ?? "");
      if (accountStatusDialogData.reason == remarkOthersKey)
        remarksend = accountStatusDialogData.remark;

      let payload = {
        email: agentDetailField.email,
        isStatusChange:
          accountStatusDialogData.status != agentDetailField.status,
        status: accountStatusDialogData.status,
      };

      if (!isNullish(accountStatusDialogData.reason))
        payload = {
          ...payload,
          lockAccountType: accountStatusDialogData.reason,
        };
      if (!isNullish(remarksend)) payload = { ...payload, remark: remarksend };

      const apiResponse = await updateStatus(payload);
      if (!apiResponse || !apiResponse?.data) {
        snackbar(t("general.message.networkError"), "error");
      } else {
        switch (apiResponse.status) {
          case 401: {
            console.log("You have no authorization to view the page");
            return logout("TOKEN_EXPIRED");
          }
          case 403: {
            snackbar(t("general.message.unauthorized"), "error");
            break;
          }
          default: {
            const responseBody = apiResponse.data;
            if (responseBody.responseCode !== "0000") {
              snackbar(
                t(DIALOG_LOCALE_KEY + "changeStatus.msgFailed") +
                  responseCodeMapping[responseBody.responseCode ?? "error"][
                    "en"
                  ],
                "error",
              );
            } else {
              snackbar(
                t(
                  DIALOG_LOCALE_KEY +
                    "changeStatus.msgSuccess." +
                    accountStatusDialogData.status.toLowerCase(),
                ),
                "success",
              );
            }
          }
        }
      }
      setSubmitting(false);
      closeAccountStatusDialog();
      initialFetchDetails();
    }
  };
  const AccountStatusDialogContent = () => {
    return (
      <AccountStatusDialog
        data={accountStatusDialogData}
        remarksList={statusRemarks}
        updatePayload={updatePayload}
      />
    );
  };

  // ===================== status dialog function end ===================== //

  return (
    <div className={s.container}>
      <div className={s.agentDetails}>
        <div className={s.detailItemContainer}>
          <div className={s.titleContent}>
            <div className={s.itemContainer}>
              <span>{IsoAgentId + " - " + agentDetailField.email}</span>
            </div>

            {IsoAgentId && (
              <div className={s.dialogActionContainer}>
                <div
                  className={[
                    s.statusContainer,
                    agentDetailField.status == "ACTIVE"
                      ? s.statusActive
                      : agentDetailField.status == "SUSPENDED"
                        ? s.statusSuspended
                        : agentDetailField.status == "TERMINATED"
                          ? s.statusTerminated
                          : s.statusUnknown,
                  ].join(" ")}
                >
                  <span>{agentDetailField.status}</span>
                </div>

                <div>
                  <div className={s.dialogButtonContainer}>
                    <button
                      className={[s.okBtn, s.functionalBtn].join(" ")}
                      disabled={submitting || detailLoading}
                      onClick={() => {
                        return openAccountStatusDialog(agentDetailField);
                      }}
                    >
                      {t(PAGE_LOCALE_KEY + `edit.label`)}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className={s.innerTabContainer}>{innerTabContainer()}</div>
      {IsoAgentId && (
        <div className={s.tabContainer}>
          <div className={selectedTab != "detail_info" ? s.inactiveTab : ""}>
            <AgentsDetailInfo
              IsoAgentId={IsoAgentId}
              updateCallback={initialFetchDetails}
            />
          </div>
          <div
            className={selectedTab != "detail_merchants" ? s.inactiveTab : ""}
          >
            <AgentsDetailMerchants
              IsoAgentId={IsoAgentId}
              IsoAgentEmail={agentDetailField?.email}
              redirectFn={redirectFn}
            />
            ;
          </div>
        </div>
      )}

      {!IsoAgentId && (
        <div className={s.contentContainer}>
          <span>{t(PAGE_LOCALE_KEY + "no_isoagentid")}</span>
        </div>
      )}

      <div className={detailLoading ? s.loadingNow : ""}>
        <SimpleDialog
          open={isAccountStatusDialogOpen}
          dialogOncloseFn={closeAccountStatusDialog}
          title={t(DIALOG_LOCALE_KEY + "changeStatus.title")}
          content={AccountStatusDialogContent()}
          positiveAction={t(DIALOG_LOCALE_KEY + "btnSubmit")}
          negativeAction={t(DIALOG_LOCALE_KEY + "btnCancel")}
          confirmFn={!submitting ? updateResultCallback : () => {}}
        />
      </div>
    </div>
  );
};

export default React.memo(AgentsDetail);
